/**
 * Creates a regex that finds email addresses within free text, such as a
 * message body or a composer input. It is not anchored, so it finds an address
 * anywhere in the text rather than validating that the whole text is one. The
 * domain may be an IPv4 literal in brackets. Quoted local parts
 * (`"john doe"@example.com`) are not recognized, and neither is an address
 * that directly follows another with no whitespace or punctuation such as `,`
 * between them (`a@x.com/b@y.com`, `mailto:a@x.com?cc=b@y.com`).
 *
 * Returns a new instance on every call, because a regex created with the `g`
 * flag keeps its `lastIndex` between calls.
 *
 * @param {string} [flags] - RegExp flags, e.g. `'g'` to iterate over every
 *   match with `exec`
 * @returns {RegExp}
 */
export function createEmailRegex(flags) {
  return new RegExp(`${LOCAL_PART_START}${LOCAL_PART}@(\\[${IPV4}\\]|${DOMAIN})`, flags);
}

/**
 * Creates a regex that finds `sip:` and `sips:` addresses within free text.
 * Like `createEmailRegex`, it is not anchored. The host may be a bare IPv4
 * address. Ports and URI parameters are not recognized.
 *
 * Returns a new instance on every call, because a regex created with the `g`
 * flag keeps its `lastIndex` between calls.
 *
 * @param {string} [flags] - RegExp flags; include `'i'` to also match an
 *   upper-case `SIP:` scheme
 * @returns {RegExp}
 */
export function createSipRegex(flags) {
  return new RegExp(`${SIP_PREFIX}${LOCAL_PART}@(${IPV4}|${DOMAIN})`, flags);
}

const DOMAIN = '([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}';
const IPV4 = '[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}';
const LOCAL_PART_CHAR = '[^<>\\(\\)\\[\\]\\\\.,;:\\s@"]';
const LOCAL_PART = `${LOCAL_PART_CHAR}+(\\.${LOCAL_PART_CHAR}+)*`;
// A match may start only where a local part can begin, so a long run without
// whitespace is scanned once instead of once per character.
const LOCAL_PART_START = `(?<!${LOCAL_PART_CHAR}\\.?)`;
const SIP_PREFIX = '(sips?:)';
